import React from "react";
import { KeyPress } from "@att-bit/duc.utils.key-press";
import {
  colorBlack,
  colorWhite,
  colorGray300,
} from "@att-bit/duc.styles.tokens";
import { Close } from "@att-bit/duc.behaviors.close";

export const Popover = ({
  position = "bottom",
  align = PopoverAlign.left,
  children,
  className,
  boxWidth,
  offset,
  show = false,
  showClose,
  smallPadding,
  fullScreen,
  popoverClassName,
  scrollOffsetTop = 30,
  ...props
}: PopoverProps) => {
  const [showState, setShowState] = React.useState(show);
  const outerPopoverRef = React.useRef<HTMLDivElement>(null);
  const boxRef = React.useRef<HTMLDivElement>(null);
  const boxRef2 = React.useRef<HTMLDivElement>(null);
  const arrowRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const headingRef = React.useRef<HTMLHeadingElement>(null);
  const firstLoad = React.useRef(true);
  let tabElements: any;
  let firstEl: any;
  let lastEl: any;
  let previousEl: any;

  // const windowSize = useWindowSize();

  const onClose = () => {
    setShowState(false);
    props.onToggle?.(false);
  };
  const handleClickOutside = (event: any) => {
    const domNode = outerPopoverRef.current;
    if (!domNode?.contains(event.target)) {
      onClose();
    }
  };
  React.useEffect(() => {
    if (window) {
      document.addEventListener("click", handleClickOutside, true);
      window.addEventListener("resize", setOffsetProps);
    }
    return () => {
      if (window) {
        document.removeEventListener("click", handleClickOutside, true);
        document.removeEventListener("focus", onFocus, true);
        window.removeEventListener("resize", setOffsetProps);
      }
    };
  }, []);

  React.useEffect(() => {
    setOffsetProps();
  }, [position]);

  // React.useLayoutEffect(() => {
  //     setOffsetProps();
  // }, [windowSize]);

  React.useEffect(() => {
    setShowState(show);
  }, [show]);

  const onFocus = (e: any) => {
    let c = false;
    if (tabElements) {
      tabElements.forEach((item: any) => {
        if (item === e.target) {
          (item as HTMLElement).focus();
          previousEl = item;
          c = true;
          return;
        }
      });
      if (
        !c &&
        e.target !== boxRef &&
        !(boxRef.current?.contains && boxRef.current?.contains(e.target))
      ) {
        if (previousEl === firstEl) {
          lastEl!.focus();
        } else {
          firstEl?.focus();
        }
      }
    }
  };

  const setOffsetProps = () => {
    if (
      !window ||
      !showState ||
      !boxRef2.current ||
      !triggerRef.current ||
      !arrowRef.current
    ) {
      return;
    }

    const currentBox = boxRef2.current;
    const currentArrow = arrowRef.current;
    const bounds = currentBox.getBoundingClientRect();
    const currentTrigger = triggerRef.current;
    const triggerBounds = currentTrigger.getBoundingClientRect();

    if (offset) {
      let assignOffset = offset;
      if (offset > bounds.width - 16) {
        assignOffset = bounds.width - 16;
      }
      document.documentElement.style.setProperty("--left", assignOffset + "px");
    } else {
      document.documentElement.style.setProperty("--left", "auto");
    }
    if (document.documentElement.clientWidth <= 320) {
      if (align === PopoverAlign.center) {
        currentArrow.style.left =
          triggerBounds.left + (triggerBounds.width / 2) + "px";
      } else if (align === PopoverAlign.left) {
        let res = triggerBounds.left + (triggerBounds.width / 2);
        res = res - bounds.left;
        currentArrow.style.left = res + "px";
      } else if (align === PopoverAlign.right) {
        const ab = bounds.width - (triggerBounds.width / 2);
        currentArrow.style.left = ab + "px";
      }
    } else {
      if (align === PopoverAlign.center) {
        const sc = currentArrow.style;
        sc.removeProperty("left");
      } else if (align === PopoverAlign.left) {
        currentArrow.style.left = (triggerBounds.width / 2) + "px";
      } else if (align === PopoverAlign.right) {
        const ab = bounds.width - triggerBounds.width / 2;
        currentArrow.style.left = ab + "px";
      }
    }

    // if it is the size of the screen
    if (bounds.left < 0) {
      currentBox.style.left = 0 + "px";
      currentArrow.style.left = triggerBounds.width / 2 + "px";
    } else if (bounds.right > document.documentElement.clientWidth) {
      currentBox.style.right = -20 + "px";
      currentArrow.style.left =
        bounds.width - triggerBounds.width / 2 - 20 + "px";
    }
  };

  const offsetViewport = (el) => {
    var box = el.getBoundingClientRect();
    var winHeight = (window.innerHeight || document.documentElement.clientHeight);
    return box.bottom - winHeight;
  };

  React.useEffect(() => {
    if (showState) {
      // headingRef.current?.focus();
      tabElements = boxRef.current?.querySelectorAll(
        'input, button, a, select, textarea, div[tabindex="0"], div[role="button"]:not([aria-hidden=true]), div[role="checkbox"]:not([aria-hidden=true]), div[role="radio"]:not([aria-hidden=true])'
      );
      document.addEventListener("focus", onFocus, true);
      if (tabElements) {
        firstEl = tabElements[0] as HTMLElement;
        lastEl = tabElements[tabElements.length - 1] as HTMLElement;
      }
      const currentBox = boxRef2.current;
      const bounds = currentBox?.getBoundingClientRect();
      if (boxRef2?.current !== null && boxRef2?.current?.scrollTo) {
        if (fullScreen && window.innerWidth <= 767) {
        } else {
          const boxCut = offsetViewport(currentBox);
          if (boxCut > 0){
            window.scrollTo({
              top: window.pageYOffset + boxCut + 5,
              behavior: "smooth",
            });
          }
        }
      }
      setOffsetProps();
    } else {
      if (firstLoad.current) {
        firstLoad.current = false;
      } else {
        (triggerRef.current?.children[0] as HTMLElement)?.focus();
      }
    }
  }, [showState]);

  const keyDownHandler = (e: React.KeyboardEvent<HTMLElement>) => {
    if (KeyPress.escIsPressed(e)) {
      hidePopover();
    } else {
      if (KeyPress.enterIsPressed(e) || KeyPress.spaceIsPressed(e)) {
        e.preventDefault();
        if (!showState) {
          showPopover();
        } else {
          hidePopover();
        }
      }
    }
  };
  const popoverKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (KeyPress.escIsPressed(e)) {
      hidePopover();
    }
  };
  let styleObj;
  if (position === "top") {
    styleObj = {
      bottom: "25px",
    };
  } else {
    styleObj = {};
  }
  let popoverClass = "";

  if (align === PopoverAlign.center) {
    popoverClass = "flex justify-center";
  } else if (align === PopoverAlign.right) {
    popoverClass = "flex justify-end";
  }

  let trigger;
  let title;
  let content;
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      switch (child.type) {
        case Popover.Trigger:
          trigger = child;
          break;
        case Popover.Content:
          content = child;
          break;
        case Popover.Title:
          title = child;
          break;
      }
    }
  });

  const popoverWrapperClass =
    align === PopoverAlign.right ? "popover-wrapper-right" : "popover-wrapper";
  return (
    <>
      <div
        className="inline-block"
        style={
          position === "top"
            ? { verticalAlign: "middle", position: "absolute", bottom: "2rem" }
            : { verticalAlign: "middle" }
        }
      >

        <div ref={outerPopoverRef}>
          <div
            ref={triggerRef}
            className={["rel", "child-container", className].join(" ")}
            onClick={onClick}
            onKeyDown={keyDownHandler}
            {...props}
          >
            {trigger}
          </div>
          <div
            ref={boxRef}
            onKeyDown={popoverKeyDown}
            className={`text-left rel ${popoverClass} ${popoverWrapperClass}`}
          >
            {showState && (
              <div
                ref={boxRef2}
                className={[
                  "popover absolute bg-white mar-t-xxs border border-gray-300 box-shadow2 pad-sm-all opacity0 text-left radius-md",
                  smallPadding ? "pad-xs-lg" : "",
                  showState ? "fadein z1" : "fadeout z-1 popover-hidden",
                  showState && fullScreen ? "popover-full" : "",
                  popoverClassName ? popoverClassName : ""
                ].join(" ")}
                style={styleObj}
                tabIndex={showState ? 0 : -1}
                aria-hidden={false}
              >
                {position === "top" ? (
                  <div
                    ref={arrowRef}
                    className={[
                      "arrow",
                      showState ? "arrow-show" : "",
                      "arrow-bottom",
                      showState && fullScreen ? "arrow-show-full" : "",
                    ].join(" ")}
                  />
                ) : null}
                { title &&
                    <h3
                      tabIndex={0}
                      ref={headingRef}
                      className="popover-heading heading-md mar-b-xs"
                    >
                      {title}
                    </h3>
                } 
                    {content}
                    {showClose && <Close
                      aria-label="Close popup"
                      onClick={onClose}
                      className={[
                        showClose ? "" : "close-hide",
                        fullScreen ? "close-button-full" : "",
                        "right-6",
                      ].join(" ")}
                      name={"close"}
                    />}
                {position === "bottom" ? (
                  <div
                    ref={arrowRef}
                    className={[
                      "arrow arrow-top",
                      showState ? "arrow-show" : "",
                      showState && fullScreen ? "arrow-show-full" : "",
                    ].join(" ")}
                  />
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        .child-container {
          line-height: 0.8;
        }

        .child-container > * {
          position: relative !important;
        }

        .child-container svg {
          vertical-align: baseline;
        }

        .popover {
          min-width: 150px;
          max-width: 400px;
          width: max-content;
        }

        .popover-hidden {
          visibility: hidden;
        }

        .popover-wrapper {
          position: relative;
        }

        .arrow {
          display: none;
          border-radius: 0 2px 0 2px;
          background-color: ${colorWhite.value};
          width: 9px;
          height: 9px;
          z-index: 1000;
          position: absolute;
        }

        .arrow-bottom {
          left: calc(50% - 4.2px);
          transform: translateY(3px) rotate(-45deg);
          box-shadow: -2px 2px 0 0px ${colorGray300.value};
          bottom: -2px;
          // top: 100%;
        }

        .arrow-top {
          left: calc(50% - 4.2px);
          box-shadow: 1px -1px 0 0px ${colorGray300.value};
          top: 0;
          transform: translateY(-5px) rotate(-45deg);
          bottom: 100%;
        }

        .arrow-show {
          display: block !important;
        }

        .popover-heading {
          color: ${colorBlack.value};
        }

        .close-hide {
          visibility: hidden;
        }

        @media (max-width: 767px) {
          .popover-full {
            position: fixed !important;
            height: 100vh !important;
            width: 100vw !important;
            border-radius: 0px;
            max-width: 100% !important;
            top: 0px;
            left: 0px !important;
            z-index: 1;
            margin-top: 0px;
            padding-top: 32px;
          }

          .close-button-full {
            top: 12px !important;
            right: 12px !important;
            min-height: 32px;
            min-width: 32px;
            visibility: visible !important;
          }

          .popover-full:before,
          .popover-full:after {
            display: none;
          }

          .arrow-show-full {
            display: none !important;
          }
        }

        @media (max-width: 428px) {
          .popover {
            min-width: 150px;
            max-width: 300px;
          }
        }

        @media (max-width: 320px) {
          .popover-wrapper {
            right: 10px;
            left: 10px;
            position: absolute;
          }
          .popover-wrapper-right {
            right: -7px;
            left: 10px;
            position: absolute;
          }
        }
      `}</style>
    </>
  );

  function onClick(e: any) {
    e.stopPropagation();
    if (showState) {
      hidePopover();
    } else {
      showPopover();
    }
  }
  function showPopover() {
    setShowState(true);
    props.onToggle?.(true);
  }
  function hidePopover() {
    setShowState(false);
    props.onToggle?.(false);
  }
};

Popover.Trigger = ({ children }) => children;
Popover.Title = ({ children }) => children;
Popover.Content = ({ children }) => children;

export enum PopoverAlign {
  left="left",
  right="right",
  center="center",
}

export type PopoverProps = {
  /**
   * Popover.Trigger will be used to render the trigger element
   * Popover.Title will be displayed as title for popover compoenent
   * Popover.Content will be displayed as content in the popover
   */
  children: React.ReactNode;
  /** Aligns the popover component. Takes values left, center, right */
  align?: PopoverAlign;
  /** To set the width of the popover content. */
  boxWidth?: number;
  /** Location of popover defaults to center. To move right, enter a positive number, to move left, enter a negative number. If number is greater than the size of the box, it defaults to center */
  offset?: number;
  /** Location popover box is placed relative to item: top | bottom. */
  position?: string;
  /** Enables show or hide for the entire popover */
  show?: boolean;
  /** Enables show or hide for the close button */
  showClose?: boolean;
  /** Opens popover in full screen mode on mobile viewport */
  fullScreen?: boolean;
  /** If set to true, shows popover in small padding(16px) */
  smallPadding?: boolean;
  /** Any additional className to add to the trigger. */
  className?: string;  
  /** Any additional className to add to this popover. */
  popoverClassName?: string;
  /** Add offset top while scrolling to top of the viewport. */
  scrollOffsetTop?: number;
  /** Stops scroll into view. by default false */
  noScroll?: boolean;
  /** Callback for the app to find out if the poppver is open or close  */
  onToggle?: (showStatus) => void;
};